import { z } from "zod";

import { contractPartyInputSchema } from "./contractParty";
import type { ParticipantType } from "./contractRolePreset";
import type { RecordAccessLevel } from "./recordAccess";

/**
 * HARİCÎ ORGANİZASYON DAVETİ şemaları (CLOS-245 `[131]`).
 *
 * Davet, Closer'da kayıtlı BAŞKA bir organizasyonu tek kanonik sözleşmeye açık rol ve
 * erişim kapsamıyla çağırır. Token yok: hedef kayıtlı bir tenant ve kabul kararını onun
 * YETKİLİ kullanıcısı kendi oturumuyla verir — e-posta bağlantısı organizasyonu temsil
 * yetkisinin yerine geçemez (kartın kuralı).
 */

/**
 * Taraf daveti (CLOS-1125 / Halit S5): public sözleşme YALNIZ `partyId + targetEmail`.
 * Ham hedef organizasyon UUID'si (`targetTenantId`, CLOS-245 kalıntısı) public API'den
 * KALDIRILDI — hangi organizasyonun kabul edeceğine ALICI kendi oturumunda karar verir (K1);
 * gönderen adresin Closer'da olup olmadığını da, hangi kuruma ait olduğunu da öğrenemez.
 * Kapsam `presetId` YA DA açık `permissions` (tam biri).
 */
export const createContractOrgInvitationSchema = z
  .object({
    partyId: z.uuid("Geçerli bir taraf seçilmelidir"),
    targetEmail: z.email("Geçerli bir e-posta adresi giriniz"),
    presetId: z.uuid("Geçerli bir preset seçilmelidir").optional(),
    permissions: z.array(z.string().min(1)).min(1).max(20).optional(),
  })
  .refine((girdi) => (girdi.presetId === undefined) !== (girdi.permissions === undefined), {
    message: "Preset ya da açık yetki seti — tam biri gönderilmelidir",
    path: ["permissions"],
  });
export type CreateContractOrgInvitationInput = z.infer<typeof createContractOrgInvitationSchema>;

/**
 * "TARAFI EKLE VE DAVET GÖNDER" (CLOS-1125 · 1156, rapor §5.1.2): taraf + davet tek adımda,
 * sunucuda AYNI transaction. E-posta zorunlu (davet adresi), kapsam preset ya da açık set.
 */
export const createContractPartyInviteSchema = contractPartyInputSchema
  .safeExtend({
    email: z.email("Davet için geçerli bir e-posta adresi gereklidir"),
    presetId: z.uuid("Geçerli bir preset seçilmelidir").optional(),
    permissions: z.array(z.string().min(1)).min(1).max(20).optional(),
  })
  .refine((girdi) => (girdi.presetId === undefined) !== (girdi.permissions === undefined), {
    message: "Preset ya da açık yetki seti — tam biri gönderilmelidir",
    path: ["permissions"],
  });
export const contractOrgParticipantParamsSchema = z.strictObject({
  id: z.uuid("Geçerli bir sözleşme kimliği gereklidir"),
  participantId: z.uuid("Geçerli bir katılım kimliği gereklidir"),
});

export type CreateContractPartyInviteInput = z.infer<typeof createContractPartyInviteSchema>;

/**
 * Davetin HUKUKİ TARAF NİTELİĞİ (CLOS-1125 / Halit S1): gönderim anında tarafın
 * `entity_type`'ından SNAPSHOT alınır; taraf sonradan değişse davet sessizce yeniden
 * yorumlanmaz. `person` → Davetlerim'de kendi adına (`self`), `organization` → kurum adına.
 */
export const CONTRACT_PARTY_PRINCIPAL_KINDS = ["person", "organization"] as const;
export type ContractPartyPrincipalKind = (typeof CONTRACT_PARTY_PRINCIPAL_KINDS)[number];

export type ContractOrgInvitationStatus =
  "pending" | "accepted" | "rejected" | "cancelled" | "expired";

/** Gönderen tarafın liste satırı. */
export type ContractOrgInvitationSummary = {
  id: string;
  contractId: string;
  /** ⚠ Kabul EDİLENE KADAR null: hedef organizasyonu alıcı seçer (K1). */
  targetTenantId: string | null;
  /** ⚠ Hedef henüz belli değilken satır adresle anlatılıyor. */
  targetOrganizationName: string | null;
  /** Davetin gittiği adres — organizasyon hedefli davetlerde null. */
  targetEmail: string | null;
  /** Davetin bağlandığı sözleşme tarafı — bağsız davetlerde null. */
  partyId: string | null;
  principalKind: ContractPartyPrincipalKind | null;
  partyDisplayName: string | null;
  permissions: string[];
  accessLevel: RecordAccessLevel;
  status: ContractOrgInvitationStatus;
  expiresAt: string;
  invitedByName: string;
  createdAt: string;
};

/**
 * Hedef tarafın GELEN davet satırı — kabul kutusunun girdisi. Sözleşmenin yalnız BAŞLIĞI
 * görünür: hedef henüz katılmadı ve davet, sözleşmenin başka hiçbir verisini açmaz.
 */
export type IncomingOrgInvitation = {
  principalKind: ContractPartyPrincipalKind | null;
  id: string;
  contractTitle: string;
  inviterOrganizationName: string;
  /**
   * Davetin ADRESLENME biçimi (K1) — ekran metni buna bağlı.
   *
   * ⚠ `"email"` olduğunda kabul, aktif organizasyon ADINA veriliyor ve kullanıcı bunu
   * ekranda okuyabilmeli: adresine gelen bir daveti hangi organizasyonu adına kabul
   * ettiğini bilmeden onaylamak, temsil yetkisini kör bir tıklamaya indirger.
   */
  addressedTo: "organization" | "email";
  permissions: string[];
  accessLevel: RecordAccessLevel;
  expiresAt: string;
  createdAt: string;
};

/** Kabulün kalıcı sonucu — CLOS-154/250 bu ilişkinin üzerine kurulacak. */
export type ContractOrgParticipantView = {
  id: string;
  contractId: string;
  participantTenantId: string;
  participantOrganizationName: string;
  permissions: string[];
  accessLevel: RecordAccessLevel;
  status: "active" | "ended";
  acceptedByName: string;
  createdAt: string;
};

/** Organizasyon davetinde kullanılabilen preset türü — kart: haricî ORGANİZASYON. */
export const ORG_INVITATION_PRESET_TYPE: ParticipantType = "external_organization";

/**
 * E-posta yoluyla adreslenmiş davetin TOKEN'LA görüntülenen hâli (CLOS-1132) — oturumsuz
 * `GET /api/org-invitations/view?token=`. Kişi davetlerinin public görünümleriyle aynı
 * sınır: davet eden organizasyon, sözleşme BAŞLIĞI, maskeli adres, erişim düzeyi ve
 * verilecek yetkiler, süre, durum. Sözleşme metni, taraflar, iç notlar yok.
 */
export type ContractOrgInvitationPublicView = {
  principalKind: ContractPartyPrincipalKind | null;
  /** Davetin hangi hukuki taraf adına olduğu (kişi adı ya da kurum unvanı). */
  partyDisplayName: string | null;
  id: string;
  invitingOrganizationName: string;
  contractTitle: string;
  /** Maskeli — `d***@firma.com`. */
  email: string;
  accessLevel: "read" | "write";
  permissions: string[];
  expiresAt: string;
  status: ContractOrgInvitationStatus;
};
